import { useEffect, useRef, useState } from "react";

const SQUARE_SDK_URLS = {
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
  production: "https://web.squarecdn.com/v1/square.js",
};

function loadSquareSdk(env) {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const url = SQUARE_SDK_URLS[env] || SQUARE_SDK_URLS.sandbox;
    let s = document.querySelector(`script[src="${url}"]`);
    if (s?.dataset.failed) { s.remove(); s = null; }
    if (!s) {
      s = document.createElement("script");
      s.src = url; s.async = true;
      s.addEventListener("error", () => { s.dataset.failed = "1"; });
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => resolve(window.Square));
    s.addEventListener("error", () => reject(new Error("Square SDK failed to load")));
  });
}

// Mounts a Square card form or Cash App Pay button into the given container ref.
export function useSquarePayments({ payCfg, method, total, containerRef, onCashAppToken }) {
  const [instance, setInstance] = useState(null);
  const [error, setError] = useState("");
  const paymentsRef = useRef(null);
  const onTokenRef = useRef(onCashAppToken);
  onTokenRef.current = onCashAppToken;

  useEffect(() => {
    const wanted = method === "square_card" || method === "cash_app_pay";
    if (!payCfg?.square_configured || !wanted || !containerRef.current) return;
    let cancelled = false;
    let inst = null;
    setInstance(null); setError("");
    (async () => {
      try {
        const Square = await loadSquareSdk(payCfg.square_environment);
        if (cancelled) return;
        if (!Square) throw new Error("Square SDK unavailable");
        const payments = Square.payments(payCfg.square_application_id, payCfg.square_location_id);
        paymentsRef.current = payments;
        if (method === "square_card") {
          inst = await payments.card();
          await inst.attach(containerRef.current);
        } else {
          const req = payments.paymentRequest({
            countryCode: "US", currencyCode: "USD",
            total: { amount: total.toFixed(2), label: "Total" },
          });
          inst = await payments.cashAppPay(req, { redirectURL: window.location.href, referenceId: `ce-${Date.now()}` });
          inst.addEventListener("ontokenization", (ev) => onTokenRef.current?.(ev.detail));
          await inst.attach(containerRef.current, { shape: "semiround", width: "full" });
        }
        if (cancelled) { inst.destroy?.(); return; }
        setInstance(inst);
      } catch (err) {
        console.error("Square SDK init failed:", err);
        if (!cancelled) setError(err?.message || "Could not load Square payment form");
      }
    })();
    return () => {
      cancelled = true;
      if (inst?.destroy) Promise.resolve(inst.destroy()).catch(() => {});
    };
  }, [method, payCfg, total, containerRef]);

  const verifyBuyer = async (token, name) => {
    try {
      const r = await paymentsRef.current?.verifyBuyer(token, {
        intent: "CHARGE", amount: total.toFixed(2), currencyCode: "USD",
        billingContact: { givenName: name },
      });
      return r?.token || null;
    } catch { return null; }
  };

  return { instance, error, verifyBuyer };
}
